import { ArrowRight02Icon, Call02Icon, CheckmarkBadge02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IProduct, phone } from 'src/constants';
import { getTel } from 'src/helpers';

interface Slide {
	eyebrow: string;
	title: string;
	desc: string;
	cta: string;
	cta2: string;
	to: string;
	to2: string;
	tagline: string;
}

const slides: Slide[] = [
	{
		eyebrow: 'D&L · TỪ 2018',
		title: 'Thiết bị điện tử công nghiệp\nchính hãng, tận tay kỹ sư.',
		desc: 'Phân phối PLC, biến tần, cảm biến, thiết bị đo lường và điện tử hàng hải cho các nhà máy, công trình tại miền Bắc.',
		cta: 'Khám phá danh mục',
		cta2: 'Yêu cầu báo giá',
		to: '/tim-kiem',
		to2: '/lien-he',
		tagline: '12 thương hiệu · 200+ mã hàng · giao tận công trình',
	},
	{
		eyebrow: 'CHUYÊN NGÀNH · HÀNG HẢI',
		title: 'Điện tử hàng hải\nđạt chuẩn SOLAS/IMO.',
		desc: 'Radar, GPS, máy đo sâu Furuno — thiết bị hàng hải cho tàu cá, tàu vận tải, dịch vụ cảng tại Hải Phòng & Quảng Ninh.',
		cta: 'Xem thiết bị hàng hải',
		cta2: 'Tư vấn kỹ thuật',
		to: '/tim-kiem',
		to2: '/lien-he',
		tagline: 'Đại lý uỷ quyền Furuno · giấy chứng nhận IMO đầy đủ',
	},
	{
		eyebrow: 'GIẢI PHÁP TỰ ĐỘNG HOÁ',
		title: 'Trọn gói từ thiết kế\nđến vận hành.',
		desc: 'Tư vấn lựa chọn PLC, biến tần, cảm biến, HMI phù hợp với từng dây chuyền. Hỗ trợ lập trình và đào tạo vận hành.',
		cta: 'Xem PLC & HMI',
		cta2: 'Gọi kỹ sư',
		to: '/tim-kiem',
		to2: `tel:${getTel()}`,
		tagline: 'Đội ngũ kỹ sư cơ điện tử với 6+ năm kinh nghiệm hiện trường',
	},
];

interface Props {
	products?: IProduct[];
}

const HeroBanner = ({ products = [] }: Props) => {
	const [idx, setIdx] = useState(0);
	const navigate = useNavigate();

	useEffect(() => {
		const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
		return () => clearInterval(t);
	}, []);

	const s = slides[idx];
	const cards = products.slice(0, 4);

	const handlePrimary = () => navigate(s.to);
	const handleSecondary = () => {
		if (s.to2.startsWith('tel:')) {
			window.location.href = s.to2;
		} else {
			navigate(s.to2);
		}
	};

	return (
		<section className='hero w-full -mt-10'>
			<div className='hero-grid-bg'></div>
			<div className='container relative z-[1]'>
				<div className='grid grid-cols-1 md:grid-cols-12 gap-8 items-center py-14'>
					<div className='md:col-span-7'>
						<div key={idx} className='fade-up'>
							<div className='inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold tracking-[0.12em] mb-5'>
								<span className='w-1.5 h-1.5 bg-cyan-400 rounded-full'></span>
								{s.eyebrow}
							</div>
							<h1 className='text-[clamp(28px,4.4vw,52px)] leading-[1.1] font-bold tracking-tight mb-[18px]' style={{ whiteSpace: 'pre-line' }}>
								{s.title}
							</h1>
							<p className='text-base leading-relaxed opacity-90 max-w-[580px] mb-7'>{s.desc}</p>
							<div className='flex flex-wrap gap-3'>
								<button onClick={handlePrimary} className='inline-flex items-center gap-2 px-6 py-3.5 rounded font-semibold text-[0.95rem] bg-white text-[#1c66a8] hover:bg-gray-100 transition'>
									{s.cta} <HugeiconsIcon icon={ArrowRight02Icon} size={16} />
								</button>
								<button
									onClick={handleSecondary}
									className='inline-flex items-center gap-2 px-6 py-3.5 rounded font-semibold text-[0.95rem] bg-white/10 border border-white/30 hover:bg-white/20 transition'
								>
									<HugeiconsIcon icon={Call02Icon} size={16} /> {s.cta2}
								</button>
							</div>
							<div className='mt-6 text-sm opacity-75 flex items-center gap-2'>
								<HugeiconsIcon icon={CheckmarkBadge02Icon} size={14} /> {s.tagline}
							</div>
						</div>
					</div>

					<div className='md:col-span-5'>
						<div className='grid grid-cols-2 gap-3'>
							{cards.map((p, i) => (
								<Link
									key={p.id}
									to={`/san-pham/${p.id}`}
									className='fade-up block rounded-[10px] p-3 bg-white/[0.08] border border-white/15 backdrop-blur-sm hover:bg-white/15 transition'
									style={{ animationDelay: `${i * 0.08}s` }}
								>
									<div className='aspect-square rounded-md overflow-hidden bg-white/10 mb-2'>
										<img src={p.thumbnail} alt={p.name} className='w-full h-full object-cover' />
									</div>
									<div className='text-[11px] opacity-70 font-semibold tracking-[0.06em] uppercase line-clamp-1'>{(p as any).brand || (p as any).category?.name || 'D&L'}</div>
									<div className='text-xs font-semibold leading-snug line-clamp-2'>{p.name}</div>
								</Link>
							))}
							{cards.length === 0 && Array.from({ length: 4 }).map((_, i) => <div key={i} className='rounded-[10px] p-3 bg-white/[0.06] border border-white/10 aspect-square' />)}
						</div>

						<div className='mt-4 text-center text-sm opacity-80'>
							Hotline:{' '}
							<a href={`tel:${getTel()}`} className='font-semibold underline'>
								{phone}
							</a>
						</div>
					</div>
				</div>

				<div className='flex justify-center gap-1.5 pb-6'>
					{slides.map((_, i) => (
						<button
							key={i}
							onClick={() => setIdx(i)}
							aria-label={`Slide ${i + 1}`}
							className='h-2 rounded-sm transition-all duration-300'
							style={{
								width: i === idx ? 32 : 8,
								background: i === idx ? 'white' : 'rgba(255,255,255,0.4)',
								border: 'none',
							}}
						/>
					))}
				</div>
			</div>
		</section>
	);
};

export default HeroBanner;
